import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { apiResetPassword, formatApiErrorDetail, setToken } from "../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const data = await apiResetPassword(token, password);
      setToken(data.access_token);
      setDone(true);
      toast.success("Password reset. Signing you in…");
      setTimeout(() => {
        // Force a full reload so AuthContext re-reads the token
        window.location.replace("/");
      }, 800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell min-h-screen flex items-center justify-center px-6 py-12">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(18,18,23,0.95)",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
            backdropFilter: "blur(12px)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      <div className="w-full max-w-md fade-up">
        <div className="text-center mb-10">
          <div
            className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.02))",
              border: "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <span className="font-serif text-[#D4AF37] text-xl">M</span>
          </div>
          <p className="eyebrow">Reset password</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F5F5] mt-3 leading-tight">
            Choose a new <span className="italic text-[#D4AF37]">key</span>
          </h1>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {!token ? (
            <div className="text-center space-y-3" data-testid="reset-invalid">
              <p className="font-serif text-2xl text-[#F5F5F5]">Missing or invalid link</p>
              <p className="text-[#8E8E93] text-sm">
                This reset link is invalid. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block mt-3 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors text-xs uppercase tracking-[0.2em]"
              >
                Request new link
              </Link>
            </div>
          ) : done ? (
            <p className="text-center text-[#8E8E93]" data-testid="reset-done">
              Signing you in…
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="eyebrow text-[10px] block mb-1">New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-luxe"
                  placeholder="At least 6 characters"
                  minLength={6}
                  required
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="eyebrow text-[10px] block mb-1">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-luxe"
                  placeholder="Type it again"
                  minLength={6}
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-gold rounded-full w-full px-6 py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                data-testid="reset-submit-btn"
              >
                {loading ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
